import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Calendar, Eye, ArrowLeft, Send, MessageSquare, Tag } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;


export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({ author_name: '', author_email: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchPost(); }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/blog/${slug}`);
      setPost(res.data);
    } catch {
      toast.error('Post not found');
    } finally { setLoading(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.author_name || !commentForm.content) {
      toast.error('Please fill in your name and comment');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/blog/${post.id}/comments`, commentForm);
      toast.success('Comment submitted for moderation');
      setCommentForm({ author_name: '', author_email: '', content: '' });
    } catch { toast.error('Failed to submit comment'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F0F4F8] py-8">
      <div className="max-w-3xl mx-auto px-4">
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Post not found</h2>
        <Link to="/blog"><Button className="bg-[#003366]">Back to Blog</Button></Link>
      </div>
    </div>
  );

  const approvedComments = (post.comments || []).filter(c => c.is_approved);

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-gradient-to-br from-[#003366] to-[#004080] text-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" />Back to Blog
          </Link>
          <Badge className="bg-white/10 text-white border-white/20 mb-4 capitalize">
            {post.category?.replace(/-/g, ' ')}
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{post.title}</h1>
          <div className="flex items-center gap-4 text-white/70 text-sm flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{post.views?.toLocaleString()} views</span>
            <span>By {post.author_name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card>
          <CardContent className="p-8">
            <div
              className="prose prose-lg max-w-none prose-headings:text-[#003366] prose-a:text-[#003366] prose-strong:text-gray-900"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
            {post.tags?.length > 0 && (
              <div className="mt-8 pt-6 border-t flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-muted-foreground" />
                {post.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comments section */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Comments ({approvedComments.length})
          </h2>

          {approvedComments.map(comment => (
            <Card key={comment.id}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm font-medium shrink-0">
                    {comment.author_name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{comment.author_name}</p>
                    <p className="text-xs text-muted-foreground mb-2">{new Date(comment.created_at).toLocaleDateString('en-GB')}</p>
                    <p className="text-sm">{comment.content}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Leave a Comment</h3>
              <form onSubmit={submitComment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input value={commentForm.author_name} onChange={e => setCommentForm(f => ({...f, author_name: e.target.value}))} placeholder="Your name" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Email (optional)</Label>
                    <Input type="email" value={commentForm.author_email} onChange={e => setCommentForm(f => ({...f, author_email: e.target.value}))} placeholder="your@email.com" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Comment *</Label>
                  <Textarea value={commentForm.content} onChange={e => setCommentForm(f => ({...f, content: e.target.value}))} rows={4} placeholder="Share your thoughts..." required />
                </div>
                <Button type="submit" disabled={submitting} className="bg-[#003366] hover:bg-[#003366]/90">
                  {submitting ? 'Submitting...' : <><Send className="w-4 h-4 mr-2" />Submit Comment</>}
                </Button>
                <p className="text-xs text-muted-foreground">Comments are moderated before appearing.</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
